import { onDomReady } from "./../../util/dom";

const HAMBURGER_SELECTOR = '.button-burger';
const HAMBURGER_IS_ACTIVE_CLASS = 'button-burger--is-active';

const PRIMARY_NAV_SELECTOR = '.navigation.navigation--primary';
const PRIMARY_NAV_OPEN_CLASSNAME = 'navigation--open';
const PRIMARY_NAV_PANEL_SELECTOR = `${PRIMARY_NAV_SELECTOR} > .navigation__nav`;

const BODY_MENU_OPEN_CLASS = 'menu-open';

const MOBILE_SUBMENU_PARENT = `.navigation__menu-item--has-children`;
const MOBILE_SUB_SUBMENU_PARENT = `.navigation__submenu-item--has-children`;
const MOBILE_MENU_OPEN_SUBMENU_PARENT = `body.${BODY_MENU_OPEN_CLASS} ${MOBILE_SUBMENU_PARENT}, body.${BODY_MENU_OPEN_CLASS} ${MOBILE_SUB_SUBMENU_PARENT}`;
const MOBILE_SUBMENU_LINK = `.navigation__menu-item-link, .navigation__submenu-item-link`;
const MOBILE_SUBMENU_LINK_TRIGGER = `.navigation__menu-item-toggle`;
const MOBILE_SUBMENU_CLASSNAME = 'navigation__submenu';
const MOBILE_SUBMENU_SELECTOR = `.${MOBILE_SUBMENU_CLASSNAME}`;
const MOBILE_SUBMENU_OPEN_CLASSNAME = `${MOBILE_SUBMENU_CLASSNAME}--open`;
const MOBILE_SUBMENU_BACK_SELECTOR = `.${MOBILE_SUBMENU_CLASSNAME}-back`;

export const mobileMenuClickEvent = 'menu:toggle';

let menuOpen = false;

type HamburgerElement = HTMLElement | null;
type PrimaryNavElement = HTMLElement | null;

let hamburger: HamburgerElement = null;
let navElement: PrimaryNavElement = null;

function getHamburger(): PrimaryNavElement {
    hamburger = hamburger ?? document.querySelector(HAMBURGER_SELECTOR) as HTMLElement | null;
    return hamburger;
}

function getNavMenu(): HamburgerElement {
    navElement = navElement ?? document.querySelector(PRIMARY_NAV_SELECTOR) as HTMLElement | null;
    return navElement;
}

function menuToggleEvent(isOpen: boolean = menuOpen): CustomEvent {
    return new CustomEvent(
        mobileMenuClickEvent,
        {
            detail: {
                isOpen,
            }
        }
    );
}

function toggleMenu(openState: boolean | null = null): void {
    menuOpen = openState ?? !menuOpen;
    document.dispatchEvent(menuToggleEvent(menuOpen));
}

function initHamburger(): void {
    hamburger = hamburger ?? document.querySelector(HAMBURGER_SELECTOR) as HTMLElement | null;

    if (!hamburger) {
        return;
    }

    hamburger.addEventListener('click', onHamburgerClick, { once: false });
}

function registerListeners(): void {
    document.addEventListener(mobileMenuClickEvent, (e: CustomEvent) => {
        e.detail.isOpen ? onMobileMenuOpen() : onMobileMenuClose();
    });
}

function registerSubmenuListeners(): void {
    const submenuParents = document.querySelectorAll<HTMLElement>(MOBILE_MENU_OPEN_SUBMENU_PARENT);

    submenuParents.forEach((parent) => {
        const link = parent.querySelector(MOBILE_SUBMENU_LINK) as HTMLElement | null;
        if (link === null) {
            return;
        }
        link.addEventListener(
            'click',
            (e) => maybeExpandSubmenu(parent, trigger as HTMLElement, e)
        );

        const trigger = parent.querySelector(MOBILE_SUBMENU_LINK_TRIGGER) as HTMLElement | null;
        if (trigger === null) {
            return;
        }
        trigger.addEventListener(
            'click',
            (e) => expandSubmenu(parent, trigger as HTMLElement)
        );
    });
}

function removeSubmenuListeners(): void {
    const submenuParents = document.querySelectorAll<HTMLElement>(MOBILE_SUBMENU_PARENT);

    submenuParents.forEach((parent) => {
        const link = parent.querySelector(MOBILE_SUBMENU_LINK) as HTMLElement | null;
        if (link !== null) {
            link.removeEventListener(
                'click',
                (e) => maybeExpandSubmenu(parent, trigger as HTMLElement, e)
            );
        }

        const trigger = parent.querySelector(MOBILE_SUBMENU_LINK_TRIGGER) as HTMLElement | null;
        if (trigger !== null) {
            trigger.removeEventListener(
                'click',
                (e) => expandSubmenu(parent, trigger as HTMLElement)
            );
        }
        const submenu = parent.querySelector(MOBILE_SUBMENU_SELECTOR) as HTMLElement | null;
        if (submenu === null) {
            return;
        }
        submenu.classList.remove(MOBILE_SUBMENU_OPEN_CLASSNAME);
        const back = parent.querySelector(MOBILE_SUBMENU_BACK_SELECTOR) as HTMLElement | null;
        if (back === null) {
            return;
        }
        const onBack = () => {
            submenu.classList.remove(MOBILE_SUBMENU_OPEN_CLASSNAME);
            back.removeEventListener('click', onBack);
        };
        back.removeEventListener('click', onBack);
    });
}

function maybeExpandSubmenu(parent: HTMLElement, trigger: HTMLElement, e): void {
    e.preventDefault();
    // only open submenu if click within trigger
    if (e.target.closest(MOBILE_SUBMENU_LINK_TRIGGER)) {
        expandSubmenu(parent, trigger);
        return;
    }
    window.location.href = e.target.href;
}

function expandSubmenu(parent: HTMLElement, trigger: HTMLElement): void {
    const submenu = parent.querySelector(MOBILE_SUBMENU_SELECTOR) as HTMLElement | null;
    if (submenu === null) {
        return;
    }
    submenu.classList.add(MOBILE_SUBMENU_OPEN_CLASSNAME);
    const back = parent.querySelector(MOBILE_SUBMENU_BACK_SELECTOR) as HTMLElement | null;
    if (back === null) {
        return;
    }
    const onBack = () => {
        submenu.classList.remove(MOBILE_SUBMENU_OPEN_CLASSNAME);
        back.removeEventListener('click', onBack);
    };
    back.addEventListener('click', onBack);
}

function onMobileMenuOpen(): void {
    addActiveClasses();
    registerSubmenuListeners();
}

function onMobileMenuClose(): void {
    removeActiveClasses();
    removeSubmenuListeners();
}

function addActiveClasses(): void {
    const nav = getNavMenu();
    const hamburger = getHamburger();

    if (!nav || !hamburger) {
        return;
    }

    hamburger.classList.add(HAMBURGER_IS_ACTIVE_CLASS);
    nav.classList.add(PRIMARY_NAV_OPEN_CLASSNAME);
    document.body.classList.add(BODY_MENU_OPEN_CLASS);

    // to allow closing when overlay clicked
    nav.addEventListener('click', checkForOverlayClick);
}

function removeActiveClasses(): void
{
    const nav = getNavMenu();
    const hamburger = getHamburger();

    if (!nav || !hamburger) {
        return;
    }

    hamburger.classList.remove(HAMBURGER_IS_ACTIVE_CLASS);
    nav.classList.remove(PRIMARY_NAV_OPEN_CLASSNAME);
    document.body.classList.remove(BODY_MENU_OPEN_CLASS);

    // remove close-on-overlay-click listener
    nav.removeEventListener('click', checkForOverlayClick);
}

function onHamburgerClick(): void
{
    toggleMenu();
}

function checkForOverlayClick(e): void
{
    // only close if click not within navigation panel
    if (!e.target.closest(PRIMARY_NAV_PANEL_SELECTOR)) {
        toggleMenu(false);
    }
}

export default () => {
    onDomReady(() => {
        initHamburger();
        registerListeners();
    });
}
